import { assertNever } from "../../../utils";
import type { ContentItem } from "../layout/documentLayout";
import { BodyDrawingEditor } from "./BodyDrawingEditor";
import { ChartQuestionEditor } from "./ChartQuestionEditor";
import { ChoiceQuestionView } from "./ChoiceQuestion";
import { ChoiceQuestionEditor } from "./ChoiceQuestionEditor";
import { FallbackQuestionView } from "./FallbackQuestion";
import { HeaderQuestionView } from "./HeaderQuestion";
import {
  IcdCodesEditor,
  IcdCodesView,
  MedicalDiagnosisEditor,
  MedicalDiagnosisView,
  MedicationProfileEditor,
  MedicationProfileView,
  PatientPhysicianEditor,
  PatientPhysicianView,
  SectionedIcdCodesEditor,
} from "./EntityQuestion";
import { BodyDrawingQuestionView, EditorQuestionView, ImageQuestionView } from "./MediaQuestion";
import { PocQuestionEditor } from "./PocQuestionEditor";
import { RNPlatformPhysicianEditor } from "./RNPlatformPhysicianEditor";
import { SignatureQuestionView } from "./SignatureQuestion";
import { SignatureQuestionEditor } from "./SignatureQuestionEditor";
import { ChartQuestionView, IcdQuestionView, PocQuestionView } from "./TableQuestion";
import { TextQuestionView } from "./TextQuestion";
import { TextQuestionEditor } from "./TextQuestionEditor";
import type { QuestionEditorProps, QuestionTypeDefinition } from "./types";

export type {
  DocumentLayoutType,
  PatientDocumentAnswer,
  QuestionEditorProps,
  QuestionTypeDefinition,
  QuestionViewProps,
} from "./types";

// Per question type: a print-style View (mimics the generated PDF) and, for the inline-editable
// types only, an Editor. Editors exist exactly for the types `isInlineEditable` accepts.

const CHOICE: QuestionTypeDefinition = { View: ChoiceQuestionView, Editor: ChoiceQuestionEditor };
const TEXT: QuestionTypeDefinition = { View: TextQuestionView, Editor: TextQuestionEditor };
const HEADER: QuestionTypeDefinition = { View: HeaderQuestionView, Editor: null };
const SIGNATURE: QuestionTypeDefinition = {
  View: SignatureQuestionView,
  Editor: SignatureQuestionEditor,
};
// The chart editor hosts its cells' editors: it gets the registry from here (no import cycle).
function ChartEditor(props: QuestionEditorProps) {
  return <ChartQuestionEditor {...props} getQuestionType={getQuestionType} />;
}
const CHART: QuestionTypeDefinition = { View: ChartQuestionView, Editor: ChartEditor };
const ICD: QuestionTypeDefinition = { View: IcdQuestionView, Editor: null };
// Entity-backed: the View prints the patient's live record, the Editor changes that record.
const CONTRACT_ICD_CODES: QuestionTypeDefinition = { View: IcdCodesView, Editor: IcdCodesEditor };
// The section's codes are not readable here; its view stays the stored answer (what a GENERAL PDF
// prints; template PDFs print no per-section list).
const SECTIONED_ICD_CODES: QuestionTypeDefinition = {
  View: IcdQuestionView,
  Editor: SectionedIcdCodesEditor,
};
const MEDICAL_DIAGNOSIS: QuestionTypeDefinition = {
  View: MedicalDiagnosisView,
  Editor: MedicalDiagnosisEditor,
};
const MEDICATION_PROFILE: QuestionTypeDefinition = {
  View: MedicationProfileView,
  Editor: MedicationProfileEditor,
};
const PATIENT_PHYSICIAN: QuestionTypeDefinition = {
  View: PatientPhysicianView,
  Editor: PatientPhysicianEditor,
};
const POC: QuestionTypeDefinition = { View: PocQuestionView, Editor: PocQuestionEditor };
const IMAGE: QuestionTypeDefinition = { View: ImageQuestionView, Editor: null };
const BODY_DRAWING: QuestionTypeDefinition = {
  View: BodyDrawingQuestionView,
  Editor: BodyDrawingEditor,
};
const EDITOR_HTML: QuestionTypeDefinition = { View: EditorQuestionView, Editor: null };
const RN_PLATFORM_PHYSICIAN: QuestionTypeDefinition = {
  View: FallbackQuestionView,
  Editor: RNPlatformPhysicianEditor,
};
const FALLBACK: QuestionTypeDefinition = { View: FallbackQuestionView, Editor: null };

export function getQuestionType(itemType: ContentItem["itemType"]): QuestionTypeDefinition {
  switch (itemType) {
    case "radio":
    case "check":
    case "yesNo":
    case "dropDown":
      return CHOICE;
    case "textShort":
    case "textLong":
    case "number":
    case "date":
    case "time":
    case "bloodPressure":
    case "weight":
      return TEXT;
    case "bigHeader":
    case "smallHeader":
      return HEADER;
    case "employeeSignature":
    case "patientSignature":
    case "customSignature":
      return SIGNATURE;
    case "chart":
      return CHART;
    case "icd":
    case "icdChart":
      return ICD;
    case "icdCodes":
    case "finalIcdCodes":
      return CONTRACT_ICD_CODES;
    case "sectionedIcdCodes":
      return SECTIONED_ICD_CODES;
    case "medicalDiagnosis":
      return MEDICAL_DIAGNOSIS;
    case "POC":
      return POC;
    case "image":
      return IMAGE;
    case "bodyDrawing":
      return BODY_DRAWING;
    case "editor":
      return EDITOR_HTML;
    case "RNPlatformPatientPhysician":
      return RN_PLATFORM_PHYSICIAN;
    case "patientPhysician":
      return PATIENT_PHYSICIAN;
    case "medicationProfile":
      return MEDICATION_PROFILE;
    case "medicationProfileItem":
    case "vbpItem":
      return FALLBACK;
    default:
      return assertNever(itemType);
  }
}
